/**
 * Text-only revision of a resolved step comment.
 * Same chrome as native TaskNoteModal in edit mode: no photos, outcome stands.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useVisualViewport } from '../hooks/useVisualViewport';
import { DICTATION_UNAVAILABLE, useWebDictation } from '../hooks/useWebDictation';
import DictationTile from '../components/DictationTile';
import VoiceLevelBars from '../components/VoiceLevelBars';

interface Props {
  shortId: string;
  initialValue: string;
  requireNote: boolean;
  sending: boolean;
  sendError: string | null;
  onConfirm: (note: string) => void;
  /** Null when the failed action is no longer queued — nothing left to retry. */
  onRetry: (() => void) | null;
  onClose: () => void;
}

const NOTE_MAX = 500;
const NOTE_MIN_HEIGHT = 80;
const NOTE_MAX_HEIGHT = 220;

const DICTATION_HINTS: Record<string, string> = {
  [DICTATION_UNAVAILABLE.NO_PERMISSION]: 'Microphone access is off, so dictation is unavailable.',
  [DICTATION_UNAVAILABLE.FAILED]: 'Dictation could not start. You can still type your note.',
};

function autosizeTextarea(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = 'auto';
  const next = Math.min(Math.max(el.scrollHeight, NOTE_MIN_HEIGHT), NOTE_MAX_HEIGHT);
  el.style.height = `${next}px`;
}

export default function RunEditNoteModal({
  shortId,
  initialValue,
  requireNote,
  sending,
  sendError,
  onConfirm,
  onRetry,
  onClose,
}: Props) {
  const [note, setNote] = useState(initialValue);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const { height: vvHeight, offsetTop: vvOffsetTop, keyboardOpen } = useVisualViewport();
  const dictation = useWebDictation({
    shortId,
    value: note,
    onChangeText: setNote,
    maxChars: NOTE_MAX,
  });

  useEffect(() => {
    autosizeTextarea(noteRef.current);
  }, [note]);

  const noteLiveRef = useRef(note);
  noteLiveRef.current = note;

  const submit = useCallback(async () => {
    if (dictation.isRecording) {
      const after = await dictation.stop();
      if (after != null) noteLiveRef.current = after;
    }
    const details = noteLiveRef.current.trim();
    if (requireNote && !details) return;
    onConfirm(details);
  }, [dictation, onConfirm, requireNote]);

  const handleClose = () => {
    if (dictation.isRecording) dictation.cancel();
    onClose();
  };

  const busy = sending || dictation.converting;
  const noteAtLimit = note.length >= NOTE_MAX;
  const confirmDisabled = busy || (requireNote && !note.trim());
  const dictationHint = dictation.unavailableReason
    ? DICTATION_HINTS[dictation.unavailableReason]
    : null;
  const showMic = dictation.supported && !dictation.converting;

  return (
    <div
      className="fixed inset-x-0 z-[80] mx-auto flex w-full max-w-md flex-col bg-white text-x9-ink"
      style={{
        height: vvHeight || '100dvh',
        top: vvOffsetTop,
      }}
    >
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pt-[max(env(safe-area-inset-top),20px)]">
        <div className="mb-1 flex items-center justify-between gap-2">
          <h1 className="min-w-0 flex-1 truncate text-lg font-bold text-x9-ink">Edit comment</h1>
          <span
            className={`min-w-[3.5rem] shrink-0 text-right text-xs tabular-nums ${
              noteAtLimit ? 'font-bold text-x9-error' : 'text-x9-marineSoft'
            }`}
          >
            {note.length}/{NOTE_MAX}
          </span>
        </div>
        <div className="mb-3 border-t border-x9-marine/10" />

        <textarea
          ref={noteRef}
          value={note}
          maxLength={NOTE_MAX}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Your comment…"
          readOnly={dictation.isRecording}
          autoFocus
          rows={3}
          className="mb-4 w-full resize-none border-0 bg-transparent p-0 text-base text-x9-ink outline-none placeholder:text-x9-marineSoft/70 focus:ring-0"
          style={{ minHeight: NOTE_MIN_HEIGHT, maxHeight: NOTE_MAX_HEIGHT }}
        />

        {dictation.mode === 'live' && dictation.isRecording ? (
          <p className="mb-4 text-xs text-x9-marineSoft">Listening…</p>
        ) : dictation.isRecording || dictation.converting ? (
          <div className="mb-4 flex items-center gap-2 text-xs text-x9-marineSoft">
            <VoiceLevelBars level={dictation.meteringLevel} active={dictation.isRecording} />
            <span>{dictation.converting ? 'Converting to text…' : 'Recording…'}</span>
          </div>
        ) : null}

        {dictationHint && !dictation.isRecording ? (
          <p className="mb-4 text-xs text-x9-marineSoft">{dictationHint}</p>
        ) : null}

        {showMic ? (
          <div className="mb-4">
            <DictationTile
              isRecording={dictation.isRecording}
              remainingSeconds={dictation.remainingSeconds}
              disabled={busy && !dictation.isRecording}
              {...dictation.pressHandlers}
            />
          </div>
        ) : null}

        {sendError ? (
          <div className="mb-3 rounded-[10px] border border-x9-error/30 bg-x9-error/5 p-3 text-sm text-x9-error">
            <p>{sendError}</p>
            {onRetry ? (
              <button type="button" className="mt-2 font-medium text-x9-marine" onClick={onRetry}>
                Retry send
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <footer
        className="shrink-0 border-t border-x9-marine/10 bg-white px-5 pt-2.5"
        style={{
          paddingBottom: keyboardOpen
            ? 10
            : 'max(env(safe-area-inset-bottom), 10px)',
        }}
      >
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            className="min-w-[60px] px-3 py-2 text-center text-base font-medium text-x9-marineSoft disabled:opacity-45"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={confirmDisabled}
            className="btn-paper-primary min-h-[48px] flex-1 !rounded-[10px]"
          >
            {sending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </footer>
    </div>
  );
}
